import { truncate } from "lodash";
import { useMemo, useState } from "react";

import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import MotionDiv from "@components/motion/motion-div";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import { nineBoxBg, nineBoxTextColor } from "./helpers";
import { NineBoxPerson } from "./types";

/**
 * The Apply or Remove filter button of a box. `inHeader` is the people
 * layout: the button sits beside the count, so a narrow box shows only icons.
 */
const NineBoxFilterButton = ({
  isFiltered,
  inHeader,
  className,
  onClick,
}: {
  isFiltered: boolean;
  inHeader: boolean;
  className?: string;
  onClick: () => void;
}) => {
  const buttonClassName = classNames(
    "shrink-0 flex items-center gap-1 max-w-full",
    className,
  );
  if (isFiltered) {
    return (
      <Button
        onClick={onClick}
        aria-label="Filter Applied, remove filter"
        className={buttonClassName}
      >
        {inHeader && (
          <TopicflowIcon type="filter" className="h-5 w-5 shrink-0" />
        )}
        <div
          className={
            inHeader
              ? "hidden @[240px]/nineboxcell:block min-w-0 truncate"
              : "flex-1 min-w-0 truncate"
          }
          title="Remove Filter"
        >
          Filter Applied
        </div>
        <TopicflowIcon type="close" className="h-5 w-5 shrink-0" />
      </Button>
    );
  }
  return (
    <Button onClick={onClick} className={buttonClassName}>
      <TopicflowIcon type="filter" className="h-5 w-5 shrink-0" />
      <div className="flex-1 min-w-0 truncate" title="Apply Filter">
        <span
          className={
            inHeader
              ? "hidden @[240px]/nineboxcell:inline"
              : "hidden @[120px]/nineboxcell:inline"
          }
        >
          Apply{" "}
        </span>
        Filter
      </div>
    </Button>
  );
};

/**
 * One box of the nine-box grid: its count and its share of the answers.
 * With `people`, the box also lists them, A-Z, under the count. The list
 * scrolls inside the box, so a full box keeps its size.
 */
const NineBoxCell = ({
  count,
  label,
  totalAssessments,
  isFiltered,
  people,
  onApplyFilter,
  onRemoveFilter,
}: {
  label?: string;
  count: number;
  totalAssessments: number;
  isFiltered: boolean;
  people?: NineBoxPerson[];
  onApplyFilter: () => void;
  onRemoveFilter: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const percentage =
    totalAssessments === 0 ? 0 : Math.floor((count / totalAssessments) * 100);

  const sortedPeople = useMemo(
    () => people && [...people].sort((a, b) => a.name.localeCompare(b.name)),
    [people],
  );

  // The count layout only: the people layout draws its own header.
  const showsPeople = !!people;
  const content = useMemo(() => {
    if (showsPeople) {
      return null;
    }
    const render = (
      <div className="flex flex-col items-center">
        {label && (
          <div
            className="text-center text-sm"
            style={{ color: nineBoxTextColor(percentage) }}
          >
            {truncate(label, { length: 70 })}
          </div>
        )}
        <div
          className="flex items-center gap-4"
          style={{ color: nineBoxTextColor(percentage) }}
        >
          <div className="font-bold text-2xl">{count}</div>
          <div className="text-sm">
            ({percentage}
            %)
          </div>
        </div>
      </div>
    );
    if (!isHovered || isFiltered || count === 0) {
      return render;
    }
    return (
      <MotionDiv
        animate={{ translateY: 0 }}
        initial={{ translateY: 10 }}
        transition={{ duration: 0.1 }}
      >
        {render}
      </MotionDiv>
    );
  }, [count, isFiltered, isHovered, label, percentage, showsPeople]);

  if (sortedPeople) {
    const textColor = nineBoxTextColor(percentage);
    return (
      <div
        aria-label="Assessment report nine box cell"
        className="h-40 flex flex-col gap-2 rounded-lg p-3 @container/nineboxcell"
        style={{ backgroundColor: nineBoxBg(percentage) }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative shrink-0 flex items-center gap-2 min-h-9">
          <div className="flex-1 min-w-0" style={{ color: textColor }}>
            {label && (
              <div className="text-sm truncate" title={label}>
                {label}
              </div>
            )}
            <div className="flex items-baseline gap-2">
              <div className="font-bold text-xl">{count}</div>
              <div className="text-sm">({percentage}%)</div>
            </div>
          </div>
          {count > 0 && !isFiltered && (
            // Always in the page, so a keyboard can reach it; shown on hover
            // or focus. It floats over the header, so the label keeps its width.
            <NineBoxFilterButton
              isFiltered={false}
              inHeader
              onClick={onApplyFilter}
              className={classNames(
                "absolute right-0 top-1/2 -translate-y-1/2",
                !isHovered &&
                  "opacity-0 pointer-events-none focus-visible:opacity-100",
              )}
            />
          )}
          {isFiltered && (
            // Narrow boxes show only the icons, so the count stays readable.
            <NineBoxFilterButton isFiltered inHeader onClick={onRemoveFilter} />
          )}
        </div>
        {sortedPeople.length > 0 && (
          <ul
            aria-label="People in this box"
            // Focusable, so a keyboard can scroll a long list.
            tabIndex={0}
            className="flex-1 min-h-0 overflow-y-auto flex flex-wrap content-start gap-1"
          >
            {sortedPeople.map((person) => (
              // White chips, so a name reads on every box colour.
              <li
                key={person.id}
                className="flex items-center gap-1 max-w-full rounded-full border border-gray-200 bg-white py-0.5 pl-0.5 pr-2 text-xs text-gray-800"
                title={person.name}
              >
                <span aria-hidden="true" className="flex shrink-0">
                  <Avatar
                    user={person}
                    size="5"
                    showTooltip={false}
                    loading="lazy"
                  />
                </span>
                <span className="truncate">{person.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div
      aria-label="Assessment report nine box cell"
      className="h-40 relative flex flex-col items-center gap-2 justify-center rounded-lg p-6 @container/nineboxcell"
      style={{ backgroundColor: nineBoxBg(percentage) }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {content}
      {isHovered && count > 0 && !isFiltered && (
        <MotionDiv
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="w-full flex justify-center"
        >
          <NineBoxFilterButton
            isFiltered={false}
            inHeader={false}
            onClick={onApplyFilter}
          />
        </MotionDiv>
      )}
      {isFiltered && (
        <NineBoxFilterButton
          isFiltered
          inHeader={false}
          onClick={onRemoveFilter}
        />
      )}
    </div>
  );
};

export default NineBoxCell;
